import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ShiftAssignDTO,
  getShiftDutiesByIdDTO,
  putShiftDutiesDTO,
  deleteShiftDutyDTO
} from "../../dtos/shift";
import shiftService from "./shiftService";

const initialState = {
  // New state properties related to shift duties
  shiftDutiesPayload: [] as any,
  shiftDutyByIdPayload: [],
  addShiftDutyPayload: null,
  updateShiftDutyPayload: null,
  deleteShiftDutyPayload: null,
  shiftDutiesActivitiesPayload: [],

  // Error flags for shift duties
  isShiftDutiesError: false,
  isAddShiftDutyError: false,
  isUpdateShiftDutyError: false,
  isDeleteShiftDutyError: false,
  isGetShiftDutyByIdError: false,
  isShiftDutiesActivitiesError: false,

  // Success flags for shift duties
  isShiftDutiesSuccess: false,
  isAddShiftDutySuccess: false,
  isUpdateShiftDutySuccess: false,
  isDeleteShiftDutySuccess: false,
  isGetShiftDutyByIdSuccess: false,
  isShiftDutiesActivitiesSuccess: false,

  // Loading flags for shift duties
  isShiftDutiesLoading: false,
  isAddShiftDutyLoading: false,
  isUpdateShiftDutyLoading: false,
  isDeleteShiftDutyLoading: false,
  isGetShiftDutyByIdLoading: false,
  isShiftDutiesActivitiesLoading: false,

  // Messages for shift duties
  shiftDutiesMessage: "",
  addShiftDutyMessage: "",
  updateShiftDutyMessage: "",
  deleteShiftDutyMessage: "",
  shiftDutyByIdMessage: "",
  shiftDutiesActivitiesMessage: "",
};

// Async Thunks for shift duty operations

// 1. Add shift duty
export const addShiftDuty = createAsyncThunk(
  "shift/duty/add",
  async (shift: ShiftAssignDTO, thunkAPI) => {
    try {
      return await shiftService.addStaffDuty(shift); // Call to the service for adding shift duty
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 2. Get all shift duties
export const getShiftDuties = createAsyncThunk(
  "shift/duties/get",
  async (id: number, thunkAPI) => {
    try {

      return await shiftService.getStaffDuties(id); // Correct API call with `params`
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 3. Get shift duty by ID
export const getShiftDutyById = createAsyncThunk(
  "shift/duty/getById",
  async (shift: getShiftDutiesByIdDTO, thunkAPI) => {
    try {
      return await shiftService.getStaffDutiesByID(shift); // Call to the service for getting shift duty by ID
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 4. Update shift duty
export const updateShiftDuty = createAsyncThunk(
  "shift/duty/update",
  async (shift: putShiftDutiesDTO, thunkAPI) => {
    try {
      return await shiftService.putStaffDutiesUpdate(shift); // Call to the service for updating shift duty
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 5. Delete shift duty
export const deleteShiftDuty = createAsyncThunk(
  "shift/duty/delete",
  async (shift: deleteShiftDutyDTO, thunkAPI) => {
    try {
      return await shiftService.deleteStaffDuty(shift); // Correct API call with `data` for DELETE request
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 6. Get all shift duties activities
export const getAllStaffDuties = createAsyncThunk(
  "shift/duties/activity/get",
  async (id: number, thunkAPI) => {
    try {

      return await shiftService.getAllStaffDuties(id); // Correct API call with `params`
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);
export const shiftSlice = createSlice({
  name: "shift",
  initialState,
  reducers: {
    reset: () => initialState,
    resetFlags: (state) => {
      state.isAddShiftDutySuccess = false;
      state.isUpdateShiftDutySuccess = false;
      state.isDeleteShiftDutySuccess = false;
      state.isUpdateShiftDutyError = false;
      state.isDeleteShiftDutyError = false;
      state.isAddShiftDutyError = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add shift duty
      .addCase(addShiftDuty.pending, (state) => {
        state.isAddShiftDutyLoading = true;
        state.isAddShiftDutySuccess = false;
        state.isUpdateShiftDutySuccess = false;
        state.isDeleteShiftDutySuccess = false;
      })
      .addCase(addShiftDuty.fulfilled, (state, action) => {
        state.isAddShiftDutyLoading = false;
        state.isAddShiftDutySuccess = true;
        state.addShiftDutyMessage = "";
        state.addShiftDutyPayload = action.payload;
      })
      .addCase(addShiftDuty.rejected, (state, action: PayloadAction<any>) => {
        state.isAddShiftDutyLoading = false;
        state.isAddShiftDutyError = true;
        state.isAddShiftDutySuccess = false;
        state.addShiftDutyMessage = action.payload;
      })

      // Get all shift duties
      .addCase(getShiftDuties.pending, (state) => {
        state.isShiftDutiesLoading = true;
      })
      .addCase(getShiftDuties.fulfilled, (state, action) => {
        state.isShiftDutiesLoading = false;
        state.isShiftDutiesSuccess = true;
        state.shiftDutiesMessage = "";
        state.shiftDutiesPayload = action?.payload;
      })
      .addCase(getShiftDuties.rejected, (state, action: PayloadAction<any>) => {
        state.isShiftDutiesLoading = false;
        state.isShiftDutiesError = true;
        state.shiftDutiesMessage = action.payload;
      })

      // Get shift duty by ID
      .addCase(getShiftDutyById.pending, (state) => {
        state.isGetShiftDutyByIdLoading = true;
      })
      .addCase(getShiftDutyById.fulfilled, (state, action) => {
        state.isGetShiftDutyByIdLoading = false;
        state.isGetShiftDutyByIdSuccess = true;
        state.shiftDutyByIdMessage = "";
        state.shiftDutyByIdPayload = action.payload;
      })
      .addCase(getShiftDutyById.rejected, (state, action: PayloadAction<any>) => {
        state.isGetShiftDutyByIdLoading = false;
        state.isGetShiftDutyByIdError = true;
        state.shiftDutyByIdMessage = action.payload;
      })

      // Update shift duty
      .addCase(updateShiftDuty.pending, (state) => {
        state.isUpdateShiftDutyLoading = true;
        state.isUpdateShiftDutySuccess = false;
        state.isDeleteShiftDutySuccess = false;
        state.isAddShiftDutySuccess = false;
      })
      .addCase(updateShiftDuty.fulfilled, (state, action) => {
        state.isUpdateShiftDutyLoading = false;
        state.isUpdateShiftDutySuccess = true;
        state.updateShiftDutyMessage = "";
        state.updateShiftDutyPayload = action.payload;
      })
      .addCase(updateShiftDuty.rejected, (state, action: PayloadAction<any>) => {
        state.isUpdateShiftDutyLoading = false;
        state.isUpdateShiftDutyError = true;
        state.isUpdateShiftDutySuccess = false;
        state.updateShiftDutyMessage = action.payload;
      })

      // Delete shift duty
      .addCase(deleteShiftDuty.pending, (state) => {
        state.isDeleteShiftDutyLoading = true;
        state.isUpdateShiftDutySuccess = false;
        state.isDeleteShiftDutySuccess = false;
        state.isAddShiftDutySuccess = false;
      })
      .addCase(deleteShiftDuty.fulfilled, (state, action) => {
        state.isDeleteShiftDutyLoading = false;
        state.isDeleteShiftDutySuccess = true;
        state.deleteShiftDutyMessage = "";
        state.deleteShiftDutyPayload = action.payload;
      })
      .addCase(deleteShiftDuty.rejected, (state, action: PayloadAction<any>) => {
        state.isDeleteShiftDutyLoading = false;
        state.isDeleteShiftDutyError = true;
        state.isDeleteShiftDutySuccess = false;
        state.deleteShiftDutyMessage = action.payload;
      })
      // Get all shift duties
      .addCase(getAllStaffDuties.pending, (state) => {
        state.isShiftDutiesActivitiesLoading = true;
      })
      .addCase(getAllStaffDuties.fulfilled, (state, action) => {
        state.isShiftDutiesActivitiesLoading = false;
        state.isShiftDutiesActivitiesSuccess = true;
        state.shiftDutiesActivitiesMessage = "";
        state.shiftDutiesActivitiesPayload = action?.payload;
      })
      .addCase(getAllStaffDuties.rejected, (state, action: PayloadAction<any>) => {
        state.isShiftDutiesActivitiesLoading = false;
        state.isShiftDutiesActivitiesError = true;
        state.shiftDutiesActivitiesMessage = action.payload;
      })
  },
});

export const { reset } = shiftSlice.actions;
export const { resetFlags } = shiftSlice.actions;
export default shiftSlice.reducer;
